import { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProfileCard } from "@/components/ProfileCard";
import { FicheProfilModal, SubscriptionRequiredModal } from "@/components/FicheProfil";
import { CATEGORIES, CITIES } from "@/lib/categories";
import { getUser } from "@/lib/user";
import { QuartierFilter } from "@/components/QuartierFilter";
import { BandeauApercu } from "@/components/BandeauApercu";
import { useSeo } from "@/lib/seo";
import { lienAnnonce, estNouvelleAnnonce } from "@/lib/annonces";
import { useApercu } from "@/lib/apercu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const cat = CATEGORIES[categoryId];
  const [annonces, setAnnonces] = useState([]);
  const [page, setPage] = useState(1);
  const [finListe, setFinListe] = useState(false);
  const [city, setCity] = useState("Tous");
  const [commune, setCommune] = useState("");
  const [quartier, setQuartier] = useState("");
  const [loading, setLoading] = useState(true);
  const requeteEnCours = useRef(0);
  const [fiche, setFiche] = useState(null);
  const [subMsg, setSubMsg] = useState(null);
  const apercu = useApercu();
  const navigate = useNavigate();
  const user = getUser();

  const voirProfil = async (profile) => {
    if (!user) {
      toast.error("Créez votre profil pour consulter les fiches.");
      navigate("/inscription");
      return;
    }
    try {
      const res = await axios.get(`${API}/profiles/${profile.id}/fiche`, { params: { viewer_id: user.id } });
      setFiche(res.data);
      if (res.data.access?.reason === "free_view") {
        toast.info("1 vue gratuite utilisée (2 maximum sans abonnement).");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "SUBSCRIPTION_REQUIRED") {
        setSubMsg(detail.message);
      } else if (err.response?.status === 401) {
        navigate("/inscription");
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur de chargement.");
      }
    }
  };

  useEffect(() => {
    setCommune("");
    setQuartier("");
  }, [city]);

  useEffect(() => {
    if (cat) axios.post(`${API}/visites/${categoryId}`).catch(() => {});
  }, [categoryId, cat]);

  useEffect(() => {
    setPage(1);
  }, [categoryId, city, commune, quartier]);

  // Référencement : un titre et une description propres à la rubrique (et à la ville).
  useSeo({
    titre: cat
      ? `${cat.name} — annonces${city !== "Tous" ? ` à ${city}` : " en Côte d'Ivoire"}`
      : null,
    description: cat
      ? `${annonces.length || ""} annonces ${cat.name} sur FodindinCa${
          city !== "Tous" ? ` à ${city}` : ""
        } : photos en clair, contact direct des annonceurs abonnés. ${cat.description || ""}`.slice(0, 155)
      : null,
    chemin: `/categorie/${categoryId}`,
  });

  // Une carte par ANNONCE (03/07/2026). Avant, la page listait les profils : un membre
  // qui avait déposé 15 annonces n'apparaissait qu'une seule fois — les annonces du
  // compte admin semblaient « disparues » alors qu'elles étaient bien actives.
  useEffect(() => {
    if (!cat) return;
    setLoading(true);
    const params = { categorie: categoryId, page };
    if (city !== "Tous") params.ville = city;
    if (commune) params.commune = commune;
    if (quartier) params.quartier = quartier;
    const jeton = ++requeteEnCours.current;
    axios
      .get(`${API}/annonces/publiques`, { params })
      .then((res) => {
        if (jeton !== requeteEnCours.current) return;  // réponse obsolète
        setAnnonces((prev) => (page === 1 ? res.data : [...prev, ...res.data]));
        setFinListe(res.data.length < 20);
      })
      .catch(() => {
        if (jeton !== requeteEnCours.current) return;
        if (page === 1) setAnnonces([]);
        setFinListe(true);
      })
      .finally(() => { if (jeton === requeteEnCours.current) setLoading(false); });
  }, [categoryId, city, commune, quartier, page, cat]);

  /** Carte d'annonce réutilisant le visuel des fiches membres. */
  const carte = (a) => {
    // Annonce déposée POUR AUTRUI (nom_annonceur saisi) : on n'affiche ni l'âge ni les
    // badges du compte déposant, qui ne sont pas ceux de la personne annoncée.
    const pourAutrui = Boolean(a.nom_annonceur);
    return {
      id: a.annonceur?.id || a.profile_id,
      annonce_id: a.id,
      firstname: a.annonceur?.firstname || "Annonceur",
      city: a.annonceur?.city,
      quartier: pourAutrui ? null : a.annonceur?.quartier,
      age: pourAutrui ? null : a.annonceur?.age,
      verified: pourAutrui ? false : a.annonceur?.verified,
      certifie_visage: pourAutrui ? false : a.annonceur?.certifie_visage,
      blind_date: pourAutrui ? false : a.annonceur?.blind_date,
      category: categoryId,
      photo: a.photos?.[0]?.url || null,
      nb_photos: a.nb_photos ?? a.photos?.length ?? 0,
      a_video: a.a_video ?? Boolean(a.video),
      description: a.texte,
    };
  };

  if (!cat) return <Navigate to="/" replace />;

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />

      <header className="relative bg-[#12100E] pt-32 pb-20 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-[130px]" style={{ background: cat.color, opacity: 0.35 }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span
              className="inline-block text-[11px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border mb-6"
              style={{ color: cat.color, borderColor: `${cat.color}50`, backgroundColor: `${cat.color}15` }}
            >
              {cat.badge}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-50">
              {cat.name}
            </h1>
            <p className="text-stone-400 mt-5 max-w-2xl leading-relaxed">{cat.description}</p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
              <span className="text-stone-500">{cat.target}</span>
              <span className="w-1 h-1 rounded-full bg-stone-600" />
              <span className="font-semibold" style={{ color: cat.color }}>{cat.pricing}</span>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="city-filter-bar">
          {CITIES.map((c) => (
            <button
              key={c}
              onClick={() => setCity(c)}
              data-testid={`profile-filter-city-${c.toLowerCase().replace("é", "e")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                city === c
                  ? "text-white border-transparent"
                  : "bg-white text-stone-600 border-[#E5DFD5] hover:border-stone-400"
              }`}
              style={city === c ? { backgroundColor: cat.color } : {}}
            >
              {c}
            </button>
          ))}
          <QuartierFilter ville={city} commune={commune} setCommune={setCommune} quartier={quartier} setQuartier={setQuartier} />
        </div>

        <BandeauApercu apercu={apercu} categorie={categoryId} />

        {loading && page === 1 ? (
          <div className="flex justify-center py-24">
            <Loader2 className="animate-spin" size={36} style={{ color: cat.color }} />
          </div>
        ) : annonces.length === 0 ? (
          <div className="text-center py-24" data-testid="empty-profiles-state">
            <p className="text-stone-500">Aucune annonce dans cette ville pour le moment.</p>
            <Link to="/inscription" className="mt-4 inline-flex items-center gap-2 font-semibold" style={{ color: cat.color }}>
              Soyez le premier à publier <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="profiles-grid">
              {annonces.map((a, i) => (
                <ProfileCard
                  key={a.id}
                  profile={carte(a)}
                  index={i}
                  onVoir={voirProfil}
                  lienAnnonce={lienAnnonce(a)}
                  nouveau={estNouvelleAnnonce(a.date_debut)}
                  testid={`annonce-card-${a.id}`}
                />
              ))}
            </div>
            {!finListe && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={loading}
                  data-testid="categorie-charger-plus"
                  className="inline-flex items-center gap-2 font-semibold px-8 py-3.5 rounded-full border-2 transition-colors"
                  style={{ borderColor: cat.color, color: cat.color }}
                >
                  {loading && <Loader2 size={15} className="animate-spin" />} Charger plus
                </button>
              </div>
            )}
          </>
        )}

        <FicheProfilModal fiche={fiche} onClose={() => setFiche(null)} />
        <SubscriptionRequiredModal message={subMsg} onClose={() => setSubMsg(null)} categoryId={categoryId} />

        <div className="mt-16 text-center">
          <Link
            to="/inscription"
            data-testid="category-register-btn"
            className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-full transition-opacity hover:opacity-90"
            style={{ backgroundColor: cat.color }}
          >
            Rejoindre {cat.name} <ArrowRight size={18} />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
